import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList } from "recharts";
import { useRecoilValue } from "recoil";
import { filtersAtom } from "../state/filter";
import { useAuth } from "./AuthContext";
import { API_BASE_URL } from "../config/api";

const priceRanges = [
    { label: "0-5k", min: 0, max: 5000 },
    { label: "5k-10k", min: 5001, max: 10000 },
    { label: "10k-15k", min: 10001, max: 15000 },
    { label: "15k-20k", min: 15001, max: 20000 },
    { label: "20k+", min: 20001, max: Infinity },
];

function normalizeDate(d: Date) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function getPurchaseDate(item: any): Date {
    return new Date(item?.transactionDate || item?.createdAt);
}

function getPriceRange(price: number): string {
    const absPrice = Math.abs(price);
    if (absPrice <= 5000) return "0-5k";
    if (absPrice <= 10000) return "5k-10k";
    if (absPrice <= 15000) return "10k-15k";
    if (absPrice <= 20000) return "15k-20k";
    return "20k+";
}

function isHiddenTransaction(item: any): boolean {
    const productPrice = parseFloat(item?.productPrice ?? "0");
    const carePrice = parseFloat(item?.carePrice ?? "0");
    const isProductNegative = productPrice < 0;
    const isCarePriceZero = carePrice === 0;
    const hasNoRoot = item?.rootUniqueId === null || item?.rootUniqueId === undefined || item?.rootUniqueId === "";
    const isUnsold = item?.sold === false;

    return isProductNegative && isCarePriceZero && hasNoRoot && isUnsold;
}

function isReplacementItem(item: any, allItems: any[]): boolean {
    if (!item) return false;
    if (["ReturnedForExchange", "cancelled"].includes(item?.status)) return false;

    const itemRoot = item?.rootUniqueId || item?.uniqueId;
    const itemEventId = item?.eventId;

    if (!itemRoot && !itemEventId) return false;

    const chainItems = allItems.filter((other: any) => {
        if (["ReturnedForExchange", "cancelled"].includes(other?.status)) return false;

        const otherRoot = other?.rootUniqueId || other?.uniqueId;
        if (itemRoot && otherRoot && itemRoot === otherRoot) return true;
        if (itemEventId && other?.eventId && itemEventId === other.eventId) return true;
        if (item?.rootUniqueId && (other?.uniqueId === item.rootUniqueId || other?.rootUniqueId === item.rootUniqueId)) return true;
        if (other?.rootUniqueId && (item?.uniqueId === other.rootUniqueId || item?.rootUniqueId === other.rootUniqueId)) return true;

        return false;
    });

    if (chainItems.length <= 1) return false;

    chainItems.sort((a: any, b: any) => {
        const dateA = new Date(a.transactionDate || a.createdAt || 0).getTime();
        const dateB = new Date(b.transactionDate || b.createdAt || 0).getTime();
        if (dateA !== dateB) return dateA - dateB;
        return (a.id || 0) - (b.id || 0);
    });

    const itemIndex = chainItems.findIndex(
        (ci: any) => (ci.id && ci.id === item.id) || (ci.uniqueId && ci.uniqueId === item.uniqueId)
    );

    return itemIndex > 0;
}

function isCarePlusReplacement(item: any, allItems: any[]): boolean {
    if (item?.status !== "created" || item?.sold !== true) return false;

    const hasLink =
        (item?.eventId != null && item.eventId !== "") ||
        (item?.rootUniqueId != null && item.rootUniqueId !== "");
    if (!hasLink) return false;

    const originalExchanged =
        (item.eventId &&
            allItems.find(
                (i: any) => i?.eventId === item.eventId && i?.status === "exchanged",
            )) ||
        allItems.find(
            (i: any) =>
                i?.status === "exchanged" &&
                (i?.uniqueId === item.rootUniqueId || i?.rootUniqueId === item.rootUniqueId),
        );

    return Boolean(originalExchanged && originalExchanged.sold === true);
}

function isOptIn(item: any, allItems: any[] = []): boolean {
    if (item?.sold === true && item?.status === "exchanged") return true;

    if (item?.sold === true && item?.status === "created") {
        const hasRoot =
            item?.rootUniqueId !== null &&
            item?.rootUniqueId !== undefined &&
            item?.rootUniqueId !== "";

        if (!hasRoot) return true;
        if (!isCarePlusReplacement(item, allItems)) return true;
    }

    return false;
}

function isCancellationReversal(item: any): boolean {
    const carePrice = parseFloat(item?.carePrice ?? 0);
    if (!(carePrice < 0 && item?.sold === false)) return false;
    if (item?.status === "ReturnedForExchange") return false;
    return item?.status === "created" || item?.status === "cancelled";
}

/**
 * ✅ NOT OPT-IN — aligned with SummaryGrid
 */
function isNotOptIn(item: any, allItems: any[] = []): boolean {
    if (item?.sold !== false) return false;

    if (
        item?.status === "ReturnedForExchange" ||
        item?.status === "cancelled" ||
        isCancellationReversal(item) ||
        isHiddenTransaction(item)
    ) {
        return false;
    }

    // Exchange replacement (not a new original order)
    if (isReplacementItem(item, allItems)) {
        return false;
    }

    return true;
}

type ChartDataItem = {
    priceRange: string;
    rate: number;
    total: number;
    optedTrue: number;
    optedFalse: number;
};

const emptyData: ChartDataItem[] = priceRanges.map((r) => ({
    priceRange: r.label,
    rate: 0,
    total: 0,
    optedTrue: 0,
    optedFalse: 0,
}));

export const OptInRateChart = () => {
    const { user } = useAuth();
    const filters = useRecoilValue(filtersAtom);
    const [data, setData] = useState<ChartDataItem[]>(emptyData);
    const [loading, setLoading] = useState(true);
    const isAdmin = user?.userName === "omkark@securedstitch.com";

    useEffect(() => {
        const fetchSales = async () => {
            try {
                let allItems: any[] = [];
                if (isAdmin) {
                    const res = await fetch(`${API_BASE_URL}/api/sale`);
                    if (!res.ok) return;
                    const allData = await res.json();
                    allItems = Array.isArray(allData) ? allData : [];
                    if (filters.member) {
                        allItems = allItems.filter((item: any) => item.memberUuid === filters.member);
                    }
                } else {
                    const memberUuid = localStorage.getItem("memberUuid") || "";
                    if (!memberUuid) return;
                    const res = await fetch(`${API_BASE_URL}/api/crd/sales`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ memberUuid }),
                    });
                    if (!res.ok) return;
                    const json = await res.json();
                    allItems = Array.isArray(json) ? json : [];
                }

                let items = allItems.filter((item: any) => !isHiddenTransaction(item));

                if (filters.fromDate && filters.toDate) {
                    const from = normalizeDate(filters.fromDate);
                    const to = normalizeDate(filters.toDate);
                    items = items.filter((item: any) => {
                        const d = normalizeDate(getPurchaseDate(item));
                        return d >= from && d <= to;
                    });
                }

                const rangeTotals: Record<string, { total: number; optedTrue: number; optedFalse: number }> =
                    {};
                priceRanges.forEach((r) => {
                    rangeTotals[r.label] = { total: 0, optedTrue: 0, optedFalse: 0 };
                });

                items.forEach((item: any) => {
                    const isOptedIn = isOptIn(item, allItems);
                    const isNotOptedIn = isNotOptIn(item, allItems);

                    // Skip returns / cancellations / pure replacements / etc.
                    if (!isOptedIn && !isNotOptedIn) return;

                    const price = parseInt(item?.productPrice) || 0;
                    const range = getPriceRange(price);

                    rangeTotals[range].total += 1;

                    if (isOptedIn) {
                        rangeTotals[range].optedTrue += 1;
                    } else if (isNotOptedIn) {
                        rangeTotals[range].optedFalse += 1;
                    }
                });

                setData(
                    priceRanges.map((r) => {
                        const g = rangeTotals[r.label];
                        return {
                            priceRange: r.label,
                            rate: g.total > 0 ? parseFloat(((g.optedTrue / g.total) * 100).toFixed(2)) : 0,
                            total: g.total,
                            optedTrue: g.optedTrue,
                            optedFalse: g.optedFalse,
                        };
                    }),
                );
            } catch (error) {
                setData(emptyData);
            } finally {
                setLoading(false);
            }
        };
        fetchSales();
        const interval = setInterval(fetchSales, 10000);
        return () => clearInterval(interval);
    }, [isAdmin, filters.member, filters.fromDate, filters.toDate]);

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (!active || !payload?.length) return null;
        const d: ChartDataItem = payload[0].payload;
        return (
            <div className="bg-[#1a1a1a] border border-[#2d2d2d] p-3 rounded-lg shadow-xl text-sm min-w-[200px]">
                <p className="text-white font-semibold mb-2 border-b border-[#2d2d2d] pb-2">
                    Price Range: {label}
                </p>
                <div className="flex justify-between gap-4 mb-1">
                    <span className="text-gray-400">Total Orders</span>
                    <span className="text-blue-400 font-medium">{d.total}</span>
                </div>
                <div className="flex justify-between gap-4 mt-2 mb-1">
                    <span className="text-gray-400">True</span>
                    <span className="text-green-400 font-medium">{d.optedTrue}</span>
                </div>
                <div className="flex justify-between gap-4 mb-2">
                    <span className="text-gray-400">False</span>
                    <span className="text-red-400 font-medium">{d.optedFalse}</span>
                </div>
                <div className="flex justify-between gap-4 border-t border-[#2d2d2d] pt-2">
                    <span className="text-gray-300 font-medium">Opt-in Rate</span>
                    <span className="text-yellow-400 font-bold text-lg">{d.rate.toFixed(1)}%</span>
                </div>
            </div>
        );
    };

    return (
        <div className="bg-neutral-900 p-6 rounded-xl w-full">
            <h2 className="text-white mb-1 font-semibold">Opt-in Rate by Price Range</h2>
            <div className="flex items-center justify-center gap-6 mt-1 flex-wrap">
                {[
                    { c: "#22c55e", l: "True" },
                    { c: "#ef4444", l: "False" },
                ].map(({ c, l }) => (
                    <div key={l} className="flex items-center gap-1.5">
                        <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: c }} />
                        <span className="text-xs text-gray-300">{l}</span>
                    </div>
                ))}
            </div>
            {loading ? (
                <div className="flex items-center justify-center h-[260px]">
                    <div className="w-5 h-5 border-2 border-gray-600 border-t-gray-300 rounded-full animate-spin" />
                </div>
            ) : (
                <div className="h-[280px] mt-3">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data} margin={{ top: 25, right: 20, left: 0, bottom: 5 }}>
                            <XAxis dataKey="priceRange" stroke="#555" tick={{ fill: "#aaa", fontSize: 12 }} />
                            <YAxis stroke="#555" tick={{ fill: "#aaa", fontSize: 11 }} />
                            <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                            <Bar dataKey="optedTrue" name="True" stackId="a" fill="#22c55e">
                                <LabelList
                                    dataKey="rate"
                                    position="top"
                                    formatter={(v: number) => (v === 0 ? "" : `${v.toFixed(1)}%`)}
                                    style={{ fill: "#facc15", fontSize: "11px", fontWeight: "bold" }}
                                />
                            </Bar>
                            <Bar dataKey="optedFalse" name="False" stackId="a" fill="#ef4444" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
};